import { Icon, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTenant } from 'src/contexts/TenantContext';

// URL to OneDrive Picker SDK (v7.2 - most stable version)
const ONEDRIVE_SDK_URL = 'https://js.live.net/v7.2/OneDrive.js';
const SCRIPT_ID = 'onedrivejs';

declare global {
  interface Window {
    OneDrive?: {
      open: (options: OneDrivePickerOptions) => void;
    };
  }
}

/**
 * Install a message event filter to prevent OneDrive SDK from crashing
 * when it receives non-OneDrive postMessage events from other sources.
 * This is a known bug in the OneDrive SDK where it assumes all messages
 * have string data and calls indexOf on them.
 */
let messageFilterInstalled = false;

function installMessageFilter() {
  if (messageFilterInstalled || typeof window === 'undefined') return;

  messageFilterInstalled = true;

  // Add a capturing event listener that stops problematic messages
  // before they reach the OneDrive SDK's listener
  window.addEventListener(
    'message',
    (event: MessageEvent) => {
      // Check if this is a problematic message for OneDrive SDK
      const data = event.data;

      // Allow messages that have indexOf (strings)
      if (typeof data === 'string') {
        return;
      }

      // Allow valid OneDrive object messages with expected properties
      if (data && typeof data === 'object') {
        // Check for common OneDrive/OAuth message properties
        if (
          'type' in data ||
          'method' in data ||
          'status' in data ||
          'code' in data ||
          'access_token' in data ||
          'error' in data
        ) {
          return;
        }
      }

      // Allow null/undefined (some legitimate messages have no data)
      if (data === null || data === undefined) {
        return;
      }

      // This message would cause OneDrive SDK to crash (e.g., number, boolean, array, etc.)
      // Stop its propagation before it reaches OneDrive's listener
      event.stopImmediatePropagation();
    },
    true, // Use capture phase to intercept before OneDrive's listener
  );
}

interface OneDriveFile {
  '@microsoft.graph.downloadUrl': string;
  name: string;
  id: string;
  size: number;
}

interface OneDrivePickerOptions {
  clientId: string;
  action: 'download' | 'share' | 'query';
  multiSelect: boolean;
  advanced?: {
    redirectUri?: string;
    filter?: string;
    isConsumerAccount?: boolean;
  };
  success: (files: { value: OneDriveFile[] }) => void;
  cancel?: () => void;
  error?: (error: any) => void;
}

/**
 * Track the promise so the SDK is fetched only once, even if multiple
 * components mount at the same time.
 */
let oneDriveLoadPromise: Promise<void> | null = null;

function loadOneDriveSdk(): Promise<void> {
  // If running in SSR / non-browser environments, skip loading.
  if (typeof window === 'undefined') return Promise.resolve();

  // Install message filter BEFORE loading SDK to catch all message events
  installMessageFilter();

  // SDK already attached to window – we're done.
  if (window.OneDrive) return Promise.resolve();

  // Reuse in-flight promise when possible.
  if (oneDriveLoadPromise) return oneDriveLoadPromise;

  oneDriveLoadPromise = new Promise<void>((resolve, reject) => {
    // Check for an existing <script> tag that might be loading/loaded already.
    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing) {
      // If script exists but OneDrive isn't available yet, wait for it
      if (window.OneDrive) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject());
      }
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = ONEDRIVE_SDK_URL;
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      // Small delay to ensure OneDrive object is fully initialized
      setTimeout(() => {
        if (window.OneDrive) {
          resolve();
        } else {
          reject(
            new Error('OneDrive SDK loaded but OneDrive object not available'),
          );
        }
      }, 100);
    };
    script.onerror = () => reject(new Error('Failed to load OneDrive SDK'));

    document.body.appendChild(script);
  });

  return oneDriveLoadPromise;
}

export interface OneDriveChooserProps {
  /** Callback fired when files are ready (as File objects) */
  onFilesReceived?: (files: File[]) => void;
  /** Callback fired when the chooser is closed without selecting */
  cancel?: () => void;
  /** Callback fired when an error occurs */
  error?: (error: any) => void;
  /** Callback fired when file processing starts (before download) */
  onProcessingStart?: (fileCount: number) => void;
  /** Allow selecting multiple files at once (default: true) */
  multiselect?: boolean;
  /** Filter file types (e.g., ".jpg,.png,.gif" for images) */
  filter?: string;
  /** Disable interaction entirely */
  disabled?: boolean;
  /** Custom trigger element */
  children?: React.ReactNode;
}

/**
 * A lightweight wrapper around the OneDrive Picker SDK making it
 * straightforward to integrate the picker in React/TypeScript apps.
 *
 * This implementation uses Microsoft's official OneDrive File Picker SDK v8.x
 * which provides better cross-browser support including Firefox and Safari.
 */
const OneDriveChooser: React.FC<OneDriveChooserProps> = ({
  onFilesReceived,
  cancel = () => {},
  error = (err) => console.error('[OneDriveChooser] Error:', err),
  onProcessingStart,
  multiselect = true,
  filter = '.jpg,.jpeg',
  disabled = false,
  children,
}) => {
  const { getConfigValue } = useTenant();
  const [isReady, setIsReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const loadAttemptedRef = useRef(false);

  const ONEDRIVE_APP_ID: string = getConfigValue(
    'MEDIA_PROVIDER_ONEDRIVE_APP_ID',
  );

  useEffect(() => {
    // Only attempt to load once
    if (loadAttemptedRef.current) return;
    loadAttemptedRef.current = true;

    loadOneDriveSdk()
      .then(() => {
        setIsReady(true);
      })
      .catch((err) => {
        console.error('[OneDriveChooser] Failed to load OneDrive SDK', err);
        setIsReady(false);
      });
  }, []);

  const onChoose = useCallback(() => {
    if (disabled || !isReady) return;

    // The SDK may not be ready yet.
    if (!window.OneDrive) {
      console.warn('[OneDriveChooser] OneDrive SDK not available');
      return;
    }

    try {
      const options: OneDrivePickerOptions = {
        clientId: ONEDRIVE_APP_ID,
        action: 'download',
        multiSelect: multiselect,
        advanced: {
          redirectUri: window.location.origin,
          filter: filter,
          // Set to true for personal OneDrive accounts, false for business
          isConsumerAccount: true,
        },
        success: async (response) => {
          if (response?.value && Array.isArray(response.value)) {
            // Notify parent that processing is starting
            onProcessingStart?.(response.value.length);

            // Download files and create File objects directly
            setIsDownloading(true);
            try {
              const downloadedFiles = await Promise.all(
                response.value.map(async (file) => {
                  try {
                    const downloadUrl = file['@microsoft.graph.downloadUrl'];

                    // Fetch the file content
                    const fileResponse = await fetch(downloadUrl);
                    if (!fileResponse.ok) {
                      throw new Error(
                        `Failed to download file: ${file.name} (status: ${fileResponse.status})`,
                      );
                    }

                    // Convert to blob
                    const blob = await fileResponse.blob();

                    // Create File object directly (no intermediate blob URL!)
                    const fileObject = new File([blob], file.name, {
                      type: blob.type || 'image/jpeg',
                    });

                    return fileObject;
                  } catch (err) {
                    console.error(`Error downloading file ${file.name}:`, err);
                    return null;
                  }
                }),
              );

              // Filter out failed downloads
              const validFiles = downloadedFiles.filter(
                (file): file is File => file !== null,
              );

              if (validFiles.length > 0 && onFilesReceived) {
                onFilesReceived(validFiles);
              }
            } catch (err) {
              console.error('[OneDriveChooser] Error processing files:', err);
              error(err);
            } finally {
              setIsDownloading(false);
            }
          }
        },
        cancel: () => {
          cancel();
        },
        error: (err) => {
          console.error('[OneDriveChooser] Picker error:', err);
          error(err);
        },
      };

      window.OneDrive.open(options);
    } catch (err) {
      console.error('[OneDriveChooser] Error opening picker:', err);
      error(err);
    }
  }, [
    disabled,
    isReady,
    ONEDRIVE_APP_ID,
    multiselect,
    filter,
    onFilesReceived,
    cancel,
    error,
    onProcessingStart,
  ]);

  return (
    <button
      type="button"
      onClick={onChoose}
      disabled={disabled || !isReady || isDownloading}
      style={{
        background: 'none',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        height: 64,
        gap: '12px',
        paddingInline: '20px',
        flexDirection: 'row',
        cursor:
          disabled || !isReady || isDownloading ? 'not-allowed' : 'pointer',
        opacity: disabled || !isReady || isDownloading ? 0.6 : 1,
        width: 150,
        justifyContent: 'center',
      }}
    >
      {children || (
        <>
          <Icon sx={{ width: 32, height: 32 }}>
            <img alt={'OneDrive'} src={'/icons/OneDriveIcon.svg'} />
          </Icon>
          <Typography>
            {isDownloading ? 'Downloading...' : 'OneDrive'}
          </Typography>
        </>
      )}
    </button>
  );
};

export default OneDriveChooser;
